import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { baseUrl, getDateWithDay  } from '../../comman/config';
import { menuAboveTable } from "../../comman/selectMenu";

export default function StockEntryScreen() {
    const [formData, setFormData] = useState({
        date: new Date(),
        ingredient_id: null,
        quantityReceived: '',
        packNos: '',
        unit: '',
        unit_name: ''
    });

    const [ingredientsList, setIngredientsList] = useState([]);
    const [lastEntries, setLastEntries] = useState([]);
    const [validationErrors, setValidationErrors] = useState({
        date: false,
        ingredient_id: false,
        quantityReceived: false,
        packNos: false
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);

    useEffect(() => {
        getIngredientsList();
    }, []);

    // Clear error and success messages after 4 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError(null);
                setSuccess(null);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    const getIngredientsList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/main_stock_received/list?condition=status=1`);
            setIngredientsList(response.data.data);
        } catch (error) {
            console.error("Error fetching ingredients:", error);
            setError("Failed to load ingredients list");
        }
    };

    const getLastEntries = async (ingredientId, date) => {
        try {
            const formattedDate = date.toISOString().split('T')[0];
            const response = await axios.get(`${baseUrl}/main_stock_received/last_entries`, {
                params: { 
                    ingredientId,
                    date: formattedDate
                }
            });

            setLastEntries(response.data.data.slice(0, 10)); // Get only latest 10 entries
            setEditingEntry(null); // Reset editing state
        } catch (error) {
            console.error("Error fetching last entries:", error);
            setError("Failed to load previous entries");
        }
    };

    const handleDateChange = (date) => {
        setFormData(prev => ({
            ...prev,
            date
        }));
        
        setValidationErrors(prev => ({
            ...prev,
            date: false
        }));

        if (formData.ingredient_id?.value) {
            getLastEntries(formData.ingredient_id.value, date);
        }
    };

    const handleIngredientChange = (selectedOption) => {
        setFormData((prevData) => ({
            ...prevData,
            ingredient_id: selectedOption,
            quantityReceived: '', // Reset quantity when ingredient changes
            packNos: '', // Reset pack numbers when ingredient changes
            unit: selectedOption?.unit || '',
            unit_name: selectedOption?.unit_name || ''
        }));

        setSelectedIngredient(selectedOption?.label || '');
        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            ingredient_id: false
        }));

        if (selectedOption?.value && formData.date) {
            getLastEntries(selectedOption.value, formData.date);
        } else {
            setLastEntries([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));

        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [name]: false
        }));
    };

    const validateForm = () => {
        const newValidationErrors = {
            date: !formData.date,
            ingredient_id: !formData.ingredient_id,
            quantityReceived: formData.quantityReceived === '',
            packNos: formData.packNos === ''
        };

        setValidationErrors(newValidationErrors);
        return !Object.values(newValidationErrors).some((error) => error);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setError("Please fill all required fields");
            return;
        }

        setShowConfirmation(true);
    };

    const confirmSave = async () => {
        try {
            const formattedDate = formData.date.toISOString().split('T')[0];
            const hours = formData.date.getHours();
            const minutes = formData.date.getMinutes();
            const timeStamp = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${hours >= 12 ? 'pm' : 'am'}`;

            const stockData = {
                ingredient_id: formData.ingredient_id.value,
                date: formattedDate,
                time_stamp: timeStamp,
                quantity: formData.quantityReceived,
                pack_nos: formData.packNos,
                unit: formData.ingredient_id.unit
            };

            const response = await axios.post(`${baseUrl}/main_stock_received/add`, stockData);

            if (response.data.status) {
                setSuccess("Stock entry saved successfully");
                // Refresh the last entries
                getLastEntries(formData.ingredient_id.value, formData.date);
                // Reset form fields except date and ingredient
                setFormData(prev => ({
                    ...prev,
                    quantityReceived: '',
                    packNos: ''
                }));
            } else {
                setError(response.data.msg || "Failed to save stock entry");
            }
        } catch (error) {
            console.error("Error saving stock entry:", error);
            setError("An error occurred while saving the stock entry");
        } finally {
            setShowConfirmation(false);
        }
    };

    const cancelSave = () => {
        setShowConfirmation(false);
    };

    const handleCancel = () => {
        setFormData({
            date: new Date(),
            ingredient_id: null,
            quantityReceived: '',
            packNos: '',
            unit: '',
            unit_name: ''
        });
        setSelectedIngredient(null);
        setLastEntries([]);
        setValidationErrors({
            date: false,
            ingredient_id: false,
            quantityReceived: false,
            packNos: false
        });
        setEditingEntry(null);
    };

    const startEditing = (entry) => {
        setEditingEntry({
            ...entry,
            newQuantity: entry.quantity,
            newPackNos: entry.pack_nos || ''
        });
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditingEntry(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const saveEdit = async () => {
        try {
            const response = await axios.post(`${baseUrl}/main_stock_received/update`, {
                id: editingEntry.id,
                quantity: editingEntry.newQuantity,
                pack_nos: editingEntry.newPackNos
            });

            if (response.data.status) {
                setSuccess("Entry updated successfully");
                getLastEntries(formData.ingredient_id.value, formData.date);
            } else {
                setError(response.data.msg || "Failed to update entry");
            }
        } catch (error) {
            console.error("Error updating entry:", error);
            setError("An error occurred while updating the entry");
        }
    };

    const cancelEdit = () => {
        setEditingEntry(null);
    };

    return (
        <>
            {/* Error Alert */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-12 py-3 rounded relative mb-4 ml-4" role="alert">
                    <span className="block sm:inline">{error}</span>
                    <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setError(null)}>
                        <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <title>Close</title>
                            <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                        </svg>
                    </span>
                </div>
            )}

            {/* Success Alert */}
            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 ml-4" role="alert">
                    <span className="block sm:inline">{success}</span>
                    <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setSuccess(null)}>
                        <svg className="fill-current h-6 w-6 text-green-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <title>Close</title>
                            <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                        </svg>
                    </span>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmation && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                        <h3 className="text-lg font-medium mb-4">Confirm Save</h3>
                        <p className="mb-6">Are you sure you want to save this stock entry?</p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelSave}
                                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmSave}
                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white shadow-md rounded-lg p-4 sm:p-12">
                <h1 className="text-xl sm:text-2xl font-bold mb-6 border-b pb-2">Receipt Entry Screen</h1>

                {/* Form Fields - Responsive Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                        <DatePicker
                            selected={formData.date}
                            onChange={handleDateChange}
                            className={`block w-full rounded-md border ${validationErrors.date ? 'border-red-500' : 'border-gray-300'} py-1.5 px-3 text-gray-900 shadow-sm placeholder:text-gray-400 sm:text-sm`}
                            dateFormat="yyyy-MM-dd"
                        />
                        {validationErrors.date && <div className="text-red-500 text-sm mt-1">Please select a date</div>}
                    </div>

                    <div>
                        <label htmlFor="ingredient" className="block text-sm font-medium text-gray-700 mb-1">Ingredient Name</label>
                        <Select
                            {...menuAboveTable}
                            className={`basic-single ${validationErrors.ingredient_id ? 'border-red-500' : ''}`}
                            classNamePrefix="select"
                            value={formData.ingredient_id}
                            onChange={handleIngredientChange}
                            options={ingredientsList.map(item => ({
                                value: item.id,
                                label: `${item.ingredient} (${item.eng_name})`,
                                unit: item.unit,
                                unit_name: item.unit_name
                            }))}
                            placeholder="Ingredient"
                            noOptionsMessage={() => "No ingredients found"}
                            isClearable
                            isSearchable={true}
                        />
                        {validationErrors.ingredient_id && <div className="text-red-500 text-sm mt-1">Please select an ingredient</div>}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label htmlFor="quantityReceived" className="block text-sm font-medium text-gray-700 mb-1">
                            Qty Received (મળેલ જથ્થો)
                        </label>
                        <div className="flex">
                            <input
                                type="number"
                                id="quantityReceived"
                                name="quantityReceived"
                                value={formData.quantityReceived}
                                onChange={handleInputChange}
                                className={`block w-full rounded-l-md border ${validationErrors.quantityReceived ? 'border-red-500' : 'border-gray-300'} py-1.5 px-3 text-gray-900 shadow-sm placeholder:text-gray-400 sm:text-sm`}
                                placeholder="Enter quantity received"
                            />
                            <span className="inline-flex items-center px-3 py-1.5 rounded-r-md border border-l-0 border-gray-300 bg-gray-100 text-gray-700 text-sm">
                                {formData.unit_name}
                            </span>
                        </div>
                        {validationErrors.quantityReceived && <div className="text-red-500 text-sm mt-1">Please enter quantity received</div>}
                    </div>

                    <div>
                        <label htmlFor="packNos" className="block text-sm font-medium text-gray-700 mb-1">Pack NOS</label>
                        <input
                            type="number"
                            id="packNos"
                            name="packNos"
                            value={formData.packNos}
                            onChange={handleInputChange}
                            className={`block w-full rounded-md border ${validationErrors.packNos ? 'border-red-500' : 'border-gray-300'} py-1.5 px-3 text-gray-900 shadow-sm placeholder:text-gray-400 sm:text-sm`}
                            placeholder="Enter number of packs"
                        />
                        {validationErrors.packNos && <div className="text-red-500 text-sm mt-1">Please enter number of packs</div>}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200 disabled:bg-blue-300"
                    >
                        Save
                    </button>
                    <button
                        onClick={handleCancel}
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring focus:ring-gray-200 disabled:bg-gray-300"
                    >
                        Cancel
                    </button>
                </div>

                {/* Previous Entries - Mobile-Friendly Design */}
                {lastEntries.length > 0 && (
                    <div className="mt-8">
                        <div className="ingredient-name mb-4">
                            <span className="font-medium">Ingredient Name: </span>
                            <span className="text-sm sm:text-base">{selectedIngredient}</span>
                        </div>

                        {/* Desktop Table View */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-300">
                                <thead>
                                    <tr>
                                        <th className="border border-gray-300 px-4 py-2 text-sm">Date</th>
                                        <th className="border border-gray-300 px-4 py-2 text-sm">Time stamp</th>
                                        <th className="border border-gray-300 px-4 py-2 text-sm">Qty. Received</th>
                                        <th className="border border-gray-300 px-4 py-2 text-sm">Pack NOS</th>
                                        <th className="border border-gray-300 px-4 py-2 text-sm">Unit</th>
                                        <th className="border border-gray-300 px-4 py-2 text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lastEntries.map((entry, index) => (
                                        <tr key={index}>
                                            <td className="border border-gray-300 px-4 py-2 text-center text-sm">{getDateWithDay(entry.entry_date)}</td>
                                            <td className="border border-gray-300 px-4 py-2 text-center text-sm">{entry.time_stamp}</td>
                                            <td className="border border-gray-300 px-4 py-2 text-center text-sm">
                                                {editingEntry && editingEntry.id === entry.id ? (
                                                    <input
                                                        type="number"
                                                        name="newQuantity"
                                                        value={editingEntry.newQuantity}
                                                        onChange={handleEditInputChange}
                                                        className="w-20 px-2 py-1 border border-gray-300 rounded"
                                                    />
                                                ) : (
                                                    entry.quantity
                                                )}
                                            </td>
                                            <td className="border border-gray-300 px-4 py-2 text-center text-sm">
                                                {editingEntry && editingEntry.id === entry.id ? (
                                                    <input
                                                        type="number"
                                                        name="newPackNos"
                                                        value={editingEntry.newPackNos}
                                                        onChange={handleEditInputChange}
                                                        className="w-20 px-2 py-1 border border-gray-300 rounded"
                                                    />
                                                ) : (
                                                    entry.pack_nos || "-"
                                                )}
                                            </td>
                                            <td className="border border-gray-300 px-4 py-2 text-center text-sm">{entry.unit_name}</td>
                                            <td className="border border-gray-300 px-4 py-2 text-center text-sm">
                                                {index === 0 && (
                                                    editingEntry && editingEntry.id === entry.id ? (
                                                        <div className="flex space-x-2 justify-center">
                                                            <button
                                                                onClick={saveEdit}
                                                                className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => startEditing(entry)}
                                                            className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                                        >
                                                            Edit
                                                        </button>
                                                    )
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden space-y-4">
                            {lastEntries.map((entry, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="font-medium text-gray-600">Date:</span>
                                            <div className="text-gray-900">{getDateWithDay(entry.entry_date)}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">Time:</span>
                                            <div className="text-gray-900">{entry.time_stamp}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">Qty. Received:</span>
                                            <div className="text-gray-900">
                                                {editingEntry && editingEntry.id === entry.id ? (
                                                    <input
                                                        type="number"
                                                        name="newQuantity"
                                                        value={editingEntry.newQuantity}
                                                        onChange={handleEditInputChange}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded mt-1"
                                                    />
                                                ) : (
                                                    entry.quantity
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">Pack NOS:</span>
                                            <div className="text-gray-900">
                                                {editingEntry && editingEntry.id === entry.id ? (
                                                    <input
                                                        type="number"
                                                        name="newPackNos"
                                                        value={editingEntry.newPackNos}
                                                        onChange={handleEditInputChange}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded mt-1"
                                                    />
                                                ) : (
                                                    entry.pack_nos || "-"
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">Unit:</span>
                                            <div className="text-gray-900">{entry.unit_name}</div>
                                        </div>
                                        <div>
                                            {index === 0 && (
                                                <div className="flex flex-col space-y-2">
                                                    {editingEntry && editingEntry.id === entry.id ? (
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={saveEdit}
                                                                className="flex-1 px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                className="flex-1 px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => startEditing(entry)}
                                                            className="w-full px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}